import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconProps } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import submitPdpInfoForm from "@store/Handlers/SubmitPdpInfoFormHandler";
import { COLORS, SPACING, TYPOGRAPHY as TYPO } from "@store/theme/design";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: OutOfMarketNoticeMailComponentStyles;
    showLabel?: boolean;
    useToasts?: boolean;
}

const mapDispatchToProps = {
    submitPdpInfoForm,
    loadCurrentShipTo,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => ({
    currentShipTo: getCurrentShipToState(state).value,
    countries: getCurrentCountries(state),
    billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
    isSigningIn: state.context.isSigningIn,
    userName: state.context.session?.userName,
    isGuest: state.context.session?.isGuest,
    session: state.context.session,
});

export interface OutOfMarketNoticeMailComponentStyles {
    warningIcon?: IconProps;
    wrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    warningText?: TypographyProps;
    messageText?: TypographyProps;
    submitButton?: ButtonPresentationProps;
}

export const outOfMarketNoticeMailComponentStyles: OutOfMarketNoticeMailComponentStyles = {
    warningIcon: {
        src: AlertTriangle,
        color: COLORS.primary.base,
        size: 22,
        css: css`
            float: left;
            margin-right: ${SPACING.xxsmall};
        `,
    },
    messageWrapper: {
        css: css`
            display: inline-block;
            overflow: auto;
            border: 2px solid ${COLORS.success};
            background: ${COLORS.successBg};
            padding: ${SPACING.xsmall};
            border-radius: 4px;
            font-weight: ${TYPO.weight.bold};
        `,
    },
    warningText: {
        size: 16,
        css: css`
            color: ${COLORS.text.light};
            margin-bottom: ${SPACING.xsmall};
        `,
    },
    submitButton: {},
};

const styles = outOfMarketNoticeMailComponentStyles;

const OutOfMarketNoticeMailComponent = ({
    productContext,
    currentShipTo,
    submitPdpInfoForm,
    userName,
    isGuest,
    session,
    loadCurrentShipTo,
    extendedStyles,
    showLabel,
    useToasts,
}: Props) => {
    if (!productContext.product.properties.isOutOfMarketMessage) {
        return <></>;
    }
    if (!userName && !isGuest) {
        return <></>;
    }

    const isAuthenticated = session && session.isAuthenticated && !session.rememberMe && !session.isGuest;
    if (!isAuthenticated) {
        return <></>;
    }
    const toasterContext = useContext(ToasterContext);
    const expandedProduct = productContext.product;
    const [styles] = React.useState(() => mergeToNew(outOfMarketNoticeMailComponentStyles, extendedStyles));
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [returnMessageInfos, setReturnMessageInfos] = useState("");
    if (!currentShipTo && isAuthenticated) {
        loadCurrentShipTo();
    }

    const handleFormSubmit = () => {
        const websiteUserEmail = session?.email;
        const payload = {
            city: currentShipTo?.city,
            state: currentShipTo?.state?.name,
            country: currentShipTo?.country?.name,
            postalCode: currentShipTo?.postalCode,
            erpNumber: expandedProduct.productNumber,
            productTitle: expandedProduct.productTitle,
            productUrl: expandedProduct.canonicalUrl,
            productImage: expandedProduct.mediumImagePath,
        };
        submitPdpInfoForm({
            formData: payload,
            onSuccess: () => {
                setReturnMessageInfos(websiteUserEmail ?? "");
                setIsEmailSent(isEmailSent => true);
                if (useToasts) {
                    toasterContext.addToast({
                        body: translate("Request sent! A G-Tech Bus Parts representative l get in touch with you."),
                        messageType: "success",
                        timeoutLength: 10000,
                    });
                }
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC
                // eslint-disable-next-line react/no-this-in-sfc
                this.onSuccess?.();
            },
        });
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            {showLabel ?? (
                <Typography {...styles.warningText} as="p">
                    <Icon {...styles.warningIcon} />
                    {expandedProduct.properties.isOutOfMarketMessage}
                </Typography>
            )}
            {!isEmailSent ? (
                <Button {...styles.submitButton} onClick={handleFormSubmit}>
                    {translate("Request availability")}
                </Button>
            ) : (
                <StyledWrapper {...styles.messageWrapper}>
                    <Typography as="p" {...styles.messageText}>
                        {translate("Request sent! A G-Tech Bus Parts representative will get in touch with you.")}
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(OutOfMarketNoticeMailComponent));
